import { useQuery } from '@tanstack/react-query';
import fetchProfitPoolData, { PROFIT_POOL_API_LIMIT, DimIdsFilter } from 'components/pages/ProfitPoolOverview/apiUtils';
import { FilterContextData } from 'contexts/FilterContext';
import { useContext, useEffect, useState, useRef } from 'react';
import { useGrowthMarginDriversContext } from '../context/GrowthMarginDriversContext';
import { useApplyFilterMutation } from 'hooks/general/useApplyFilterMutation';
import { generateDefaultApplyFilter } from 'utils/helperMethods';
import { getIntersectionOnApplyFilter } from 'utils/helperMethods';
import { FundFlowTreeNode } from '../data/fundFlowData';
import _ from 'lodash';

interface UseDecompositionDataParams {
    parentAttributeType: string;
    parentDimension: string;
    nextAttributeType: string;
    nextDimension: string;
    nextIsAttribute?: boolean; // Add this
    enabled?: boolean;
    nodeData?: FundFlowTreeNode | null;
}

const useDecompositionData = ({
    parentAttributeType,
    parentDimension,
    nextAttributeType,
    nextDimension,
    nextIsAttribute = true, // Add this with default value
    enabled = false,
    nodeData,
}: UseDecompositionDataParams) => {
    const { selectedGrowthMarginDriver } = useGrowthMarginDriversContext();
    const {
        idForRestOfComponent,
        timePeriodSelection,
        singleSelectedTier,
        allDimAndAttributeTypeList,
        timePeriodLoading,
    } = useContext(FilterContextData);

    // Initialize dimIdsFilter: start as idForRestOfComponent, will be reset to undefined when apply filter is needed
    const [dimIdsFilter, setDimIdsFilter] = useState<DimIdsFilter | undefined>(idForRestOfComponent);
    const [applyFilterLoading, setApplyFilterLoading] = useState(false);
    const [applyFilterCompleted, setApplyFilterCompleted] = useState(true);

    // Track the last processed nodeData to prevent duplicate calls
    const lastProcessedNodeRef = useRef<string | null>(null);
    // Store latest idForRestOfComponent in ref to avoid dependency issues
    const idForRestOfComponentRef = useRef(idForRestOfComponent);

    // Update ref when idForRestOfComponent changes
    useEffect(() => {
        idForRestOfComponentRef.current = idForRestOfComponent;
    }, [idForRestOfComponent]);

    // Handler for apply filter completion
    const onApplyFilterComplete = (payload: any) => {
        if (!payload) {
            setDimIdsFilter(idForRestOfComponentRef.current);
            setApplyFilterLoading(false);
            setApplyFilterCompleted(true);
            return;
        }

        // Convert apply filter response to dimIdsFilter format
        const applyFilterResponse: DimIdsFilter = {
            dim1: { ids: payload.dim1?.ids || [] },
            dim2: { ids: payload.dim2?.ids || [] },
            dim3: { ids: payload.dim3?.ids || [] },
            dim4: { ids: payload.dim4?.ids || [] },
        };

        // Intersect with existing idForRestOfComponent filter
        const intersectedFilter = getIntersectionOnApplyFilter(idForRestOfComponentRef.current, applyFilterResponse);
        setDimIdsFilter(intersectedFilter);
        setApplyFilterLoading(false);
        setApplyFilterCompleted(true);
    };

    const { applyFilterMutate } = useApplyFilterMutation({
        onComplete: onApplyFilterComplete,
    });

    // Reset dimIdsFilter when enabled becomes false
    useEffect(() => {
        if (!enabled) {
            setDimIdsFilter(idForRestOfComponent);
            setApplyFilterLoading(false);
            setApplyFilterCompleted(true);
            lastProcessedNodeRef.current = null;
        }
    }, [enabled]);

    // Call apply filter API when nodeData is available
    useEffect(() => {
        // Create a unique key for the node to track if we've already processed it
        const nodeKey = nodeData ? `${nodeData.attributeType}-${nodeData.dimension}-${nodeData.attributeExId}` : null;

        if (enabled && nodeData && nodeData.attributeType && nodeData.dimension && nodeData.attributeExId) {
            // Only call if this is a different node than the last one processed
            if (lastProcessedNodeRef.current === nodeKey) {
                return; // Already processed this node
            }

            lastProcessedNodeRef.current = nodeKey;
            setApplyFilterLoading(true);
            setApplyFilterCompleted(false);
            // Reset dimIdsFilter to undefined so query won't run until apply filter completes
            setDimIdsFilter(undefined);

            // Prepare apply filter variables using ref to get latest value
            const defaultApplyFilterGenerated = generateDefaultApplyFilter(idForRestOfComponentRef.current);
            const tempApplyFilterVariable: any = _.cloneDeep(defaultApplyFilterGenerated);

            // Add the node's attribute to the appropriate dimension's inclusion array
            const nodeFilterObject = {
                attributeType: nodeData.attributeType,
                attributeExId: nodeData.attributeExId,
                attributeDesc: nodeData.name,
                dimension: nodeData.dimension,
            };

            const dimensionKey = `jsonstring_${nodeData.dimension}`;
            if (tempApplyFilterVariable[dimensionKey]) {
                tempApplyFilterVariable[dimensionKey].inclusion = [nodeFilterObject];
            } else {
                tempApplyFilterVariable[dimensionKey] = {
                    inclusion: [nodeFilterObject],
                };
            }

            // Stringify all dimension variables
            tempApplyFilterVariable.jsonstring_dim1 = JSON.stringify(tempApplyFilterVariable.jsonstring_dim1 || {});
            tempApplyFilterVariable.jsonstring_dim2 = JSON.stringify(tempApplyFilterVariable.jsonstring_dim2 || {});
            tempApplyFilterVariable.jsonstring_dim3 = JSON.stringify(tempApplyFilterVariable.jsonstring_dim3 || {});
            tempApplyFilterVariable.jsonstring_dim4 = JSON.stringify(tempApplyFilterVariable.jsonstring_dim4 || {});

            // Call apply filter mutation
            applyFilterMutate({ variables: tempApplyFilterVariable });
        } else if (enabled && !nodeData) {
            // If no nodeData, use existing idForRestOfComponent
            setDimIdsFilter(idForRestOfComponentRef.current);
            setApplyFilterLoading(false);
            setApplyFilterCompleted(true);
            lastProcessedNodeRef.current = null;
        }
    }, [enabled, nodeData?.attributeType, nodeData?.dimension, nodeData?.attributeExId, applyFilterMutate]);

    const { data, isPending, isError, isSuccess, refetch } = useQuery({
        queryKey: [
            'decomposition-data',
            parentAttributeType,
            parentDimension,
            nextAttributeType,
            nextDimension,
            nextIsAttribute, // Add this to query key
            singleSelectedTier,
            dimIdsFilter,
            timePeriodSelection,
            selectedGrowthMarginDriver,
        ],
        queryFn: ({ signal }) => {
            return fetchProfitPoolData({
                selectedSubFilter: {
                    dataKey: nextAttributeType,
                    dimension: nextDimension,
                    isAttribute: nextIsAttribute ?? true, // Use nextIsAttribute instead of hardcoded true
                },
                limit: PROFIT_POOL_API_LIMIT,
                offset: 0,
                kpi: selectedGrowthMarginDriver?.key || 'profit_absolute',
                tierList: singleSelectedTier,
                yearAgo: false,
                groupedByList: ['attribute'],
                dimIdsFilter: dimIdsFilter || idForRestOfComponent,
                frequency: 'M',
                timePeriod: timePeriodSelection ? JSON.parse(timePeriodSelection) : undefined,
                signal,
                parentAttributeType,
                parentDimension,
            });
        },
        enabled:
            enabled &&
            Boolean(
                allDimAndAttributeTypeList &&
                    'loading' in allDimAndAttributeTypeList &&
                    !timePeriodLoading &&
                    timePeriodSelection &&
                    parentAttributeType &&
                    nextAttributeType &&
                    !applyFilterLoading &&
                    applyFilterCompleted &&
                    dimIdsFilter !== undefined,
            ),
    });

    return {
        data,
        isPending: isPending || applyFilterLoading,
        isError,
        isSuccess,
        refetch,
    };
};

export default useDecompositionData;
